import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { dataViewI18nT } from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import {
  hasSystemDocBarrier,
  runSystemDocBarrier,
  type SystemDocBarrierContext,
} from '@blocksuite/store';
import { canonicalDatabaseRowOrder } from '@streamlain/database-identity';
import { encodeStateVector, Map as YMap } from 'yjs';

import type {
  DatabaseRowMutation,
  RowShardCells,
} from './data-source-contracts.js';
import {
  type DatabasePublicationViewState,
  type DatabaseRowShardHydrationProgress,
  type DatabaseRowShardIntegrityIssue,
  type DatabaseRowShardReadiness,
  type DatabaseRowShardRepairResult,
} from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import {
  getLoadedShardDirectory,
  missingShardDirectoryWindowParts,
  requireCompleteShardDirectory,
  requireLoadedShardDirectory,
  selectShardsForRowsWindow,
} from './data-source-shard-directory.js';
import { hasPendingWriteShardDocument } from './data-source-shard-ownership.js';
import { claimRowPublication } from './data-source-shard-writing.js';
import {
  INITIAL_DATABASE_ROW_WINDOW_LIMIT,
  observeDatabasePublicationView,
} from './data-source-values.js';

export function materializeAllShardedRows(this: DatabaseSourceRuntime): void {
  const directory = this.getCompleteShardDirectory();
  if (directory.status !== 'ready') return;
  const manifest = directory.manifest;

  const expectedRows = this.getManifestRowCount(manifest);
  if (
    this._shardedRefreshFrame === null &&
    this._shardedRowIds.length === expectedRows &&
    this._shardedRowsById.size === expectedRows &&
    this.areAllManifestShardRowsMaterialized(manifest)
  ) {
    return;
  }

  for (const shard of manifest.shards) {
    this.ensureShardObserved(shard, 'relation');
  }
  this.refreshShardedRowsFromDocs(manifest.shards);
}

export function getManifestRowCount(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>
): number {
  if (!manifest) return 0;
  return manifest.shards.reduce((sum, shard) => sum + shard.rowCount, 0);
}

export function getManifestShardsForRowsWindow(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>,
  offset: number,
  limit: number
): DatabaseShardManifestEntry[] {
  if (!manifest) return [];
  return selectShardsForRowsWindow(manifest.shards, 0, offset, limit);
}

export function getInitialRowWindowReadiness(
  this: DatabaseSourceRuntime,
  limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
): DatabaseRowShardReadiness {
  this._shardedRowsRevision.value;
  const directory = getLoadedShardDirectory.call(this, limit);
  // The first rows show once the directory parts holding them are loaded;
  // the other parts follow in the background.
  if (
    directory.missingParts.some(part =>
      this._missingRowShardDocIds.has(part.partDocId)
    )
  )
    return missingDataReadiness();
  if (missingShardDirectoryWindowParts(directory, limit).length)
    return { status: 'loading' };
  const manifest = directory.manifest;

  const integrityIssue = this.getAuthoritativeRowShardIntegrityIssue(manifest);
  if (integrityIssue) {
    return {
      message: integrityIssue.message,
      repairable: integrityIssue.repairable,
      status: 'error',
    };
  }
  const activeShards = this.getActiveWindowManifestShards(manifest);
  const initialShards = activeShards.length
    ? activeShards
    : this.isFileIndexDatabase() && this.getManifestRowCount(manifest) === 0
      ? manifest.shards.slice(0, 1)
      : this.getManifestShardsForRowsWindow(manifest, 0, limit);
  // A shard the server confirmed missing never passes the pending integrity
  // validation below, so it is reported before that check.
  const missingShardReadiness = getMissingRowShardReadiness.call(
    this,
    initialShards
  );
  if (missingShardReadiness) return missingShardReadiness;

  // Root and window shards render only from the same published state: a
  // newer announced state of any of them holds the display back, even when
  // the loaded row counts already match (moves, delete-only changes).
  const publicationReadiness = getPublicationViewReadiness.call(this, [
    manifest.databaseRootDocId,
    ...initialShards.flatMap(shard => this.getShardDocIds(shard)),
  ]);
  if (publicationReadiness) {
    // The pull that makes these docs current needs their loads requested.
    for (const shard of initialShards) {
      this.ensureShardObserved(shard, 'row-window');
    }
    return publicationReadiness;
  }

  const pendingIntegrityIssue = this.getLoadedRowShardIntegrityIssue(manifest);
  if (pendingIntegrityIssue) {
    this.scheduleLoadedRowShardIntegrityValidation(
      manifest,
      pendingIntegrityIssue
    );
    return { status: 'loading' };
  }

  // Readiness owns the load it waits for. A cold workspace can already have
  // the shard doc registered locally while its remote snapshot has not been
  // requested yet. Inspecting that empty shell before requesting the initial
  // row window creates a deadlock: callers keep polling `loading`, but no
  // observer ever triggers the row-shard load.
  for (const shard of initialShards) {
    this.ensureShardObserved(shard, 'row-window');
  }

  const emptyIntegrityIssue = this.getLoadedRowShardIntegrityIssue(
    manifest,
    true
  );
  if (emptyIntegrityIssue) {
    const snapshot = this.captureLoadedRowShardSnapshot(manifest, true);
    if (snapshot && this._rowShardEmptyConfirmedSnapshot === snapshot) {
      this.scheduleLoadedRowShardManifestAutoRepair(
        manifest,
        emptyIntegrityIssue
      );
      return {
        message: emptyIntegrityIssue.message,
        repairable: emptyIntegrityIssue.repairable,
        status: 'error',
      };
    }
    this.scheduleLoadedEmptyRowShardConfirmation(
      manifest,
      emptyIntegrityIssue,
      snapshot
    );
    return { status: 'loading' };
  }
  if (
    directory.totalRowCount === 0 &&
    (!this.isFileIndexDatabase() || manifest.shards.length === 0)
  ) {
    return { status: 'ready' };
  }

  if (initialShards.length === 0) {
    return { status: 'ready' };
  }

  let ready = true;
  for (const shard of initialShards) {
    const doc = this.ensureShardObserved(shard, 'row-window');
    if (!doc.ready || !this.hasCompleteLoadedShardRows(shard, doc)) {
      ready = false;
    }
  }

  if (ready) {
    this.refreshShardedRowsFromDocs(initialShards);
  }

  return ready ? { status: 'ready' } : { status: 'loading' };
}

/**
 * A shard the server confirmed it does not hold never becomes ready, so it
 * is reported as missing instead of loading forever. A shard that arrives
 * later (another device syncs it) is ready again and clears the state. A
 * shard this source is writing into is on its way to the server, not missing.
 */
function getMissingRowShardReadiness(
  this: DatabaseSourceRuntime,
  shards: readonly DatabaseShardManifestEntry[]
): DatabaseRowShardReadiness | null {
  const missing = shards.some(shard => {
    if (
      shard.rowCount === 0 ||
      this.hasLoadedShardRows(this.getExistingShardDoc(shard))
    ) {
      return false;
    }
    const docIds = this.getShardDocIds(shard);
    return (
      docIds.length > 0 &&
      docIds.every(
        docId =>
          this._missingRowShardDocIds.has(docId) &&
          !hasPendingWriteShardDocument(this, docId)
      )
    );
  });
  return missing ? missingDataReadiness() : null;
}

function missingDataReadiness(): DatabaseRowShardReadiness {
  return {
    message: dataViewI18nT('database.dataMissing.description'),
    missing: true,
    repairable: false,
    status: 'error',
  };
}

/**
 * Loading while any of `docIds` has a newer announced state than this
 * device pulled, flagged incomplete once a read of one failed terminally.
 */
function getPublicationViewReadiness(
  this: DatabaseSourceRuntime,
  docIds: readonly string[]
): DatabaseRowShardReadiness | null {
  let pending = false;
  for (const docId of docIds) {
    const state = observePublicationViewState.call(this, docId);
    if (state === 'incomplete') return { incomplete: true, status: 'loading' };
    if (state !== 'current') pending = true;
  }
  return pending ? { status: 'loading' } : null;
}

/** Last reported state of `docId`, undefined until its observer reports. */
function observePublicationViewState(
  this: DatabaseSourceRuntime,
  docId: string
): DatabasePublicationViewState | undefined {
  if (!this._publicationViewDisposers.has(docId)) {
    // Reserved before observing: observers may report synchronously.
    this._publicationViewDisposers.set(docId, () => {});
    const dispose = observeDatabasePublicationView(
      this._hostModel.store,
      docId,
      state => {
        if (this._disposed || this._publicationViewStates.get(docId) === state)
          return;
        this._publicationViewStates.set(docId, state);
        // Observers may report while a render reads readiness.
        queueMicrotask(() => {
          if (!this._disposed) this._shardedRowsRevision.value += 1;
        });
      }
    );
    this._publicationViewDisposers.set(docId, dispose);
  }
  return this._publicationViewStates.get(docId);
}

export function getContentReadiness(
  this: DatabaseSourceRuntime
): DatabaseRowShardReadiness {
  // Canvas renders the same bounded window as other database surfaces.
  const readiness = this.getInitialRowWindowReadiness();
  const manifest = getLoadedShardDirectory.call(
    this,
    INITIAL_DATABASE_ROW_WINDOW_LIMIT
  ).manifest;
  if (readiness.status === 'error') return readiness;
  const active = this.getActiveWindowManifestShards(manifest);
  const windowShards = active.length
    ? active
    : this.getManifestShardsForRowsWindow(
        manifest,
        0,
        INITIAL_DATABASE_ROW_WINDOW_LIMIT
      );
  return {
    ...readiness,
    progress: this.getRowShardHydrationProgress({
      ...manifest,
      shards: windowShards,
    }),
  };
}

export function isInitialRowWindowReady(
  this: DatabaseSourceRuntime,
  limit: number = INITIAL_DATABASE_ROW_WINDOW_LIMIT
): boolean {
  return this.getInitialRowWindowReadiness(limit).status === 'ready';
}

export function getLoadedRowShardIntegrityIssue(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  includeEmptyManifestMismatch = false
): DatabaseRowShardIntegrityIssue | null {
  if (this.isFileIndexDatabase()) return null;

  for (const shard of manifest.shards) {
    // Every writer records a row's fingerprint with the row.
    if (shard.rowCount > 0 && !shard.rowFingerprints)
      return {
        message: `Database "${this.databaseId}" cannot load because row shard "${shard.shardId}" holds ${shard.rowCount} rows without row fingerprints.`,
        repairable: false,
        rowCount: shard.rowCount,
        shardId: shard.shardId,
      };
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) continue;

    const rowOrder = canonicalDatabaseRowOrder(
      shardDoc.spaceDoc
        .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
        .toArray()
        .map(value => String(value))
    );
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const uniqueRowIds = new Set(rowOrder);
    const missingRowIds = rowOrder.filter(rowId => !rowsMap.has(rowId));
    const unindexedRowIds = [...rowsMap.keys()].filter(
      rowId => !uniqueRowIds.has(String(rowId))
    );
    const boundaryMetadataMatches =
      shard.firstRowId === rowOrder[0] && shard.lastRowId === rowOrder.at(-1);

    // A newly registered shard can be ready before its local/remote snapshot
    // lands. Empty state remains loading; non-empty contradictory state is a
    // persisted integrity failure and must not spin forever as "loading".
    if (
      !includeEmptyManifestMismatch &&
      rowOrder.length === 0 &&
      rowsMap.size === 0 &&
      shard.rowCount > 0
    ) {
      continue;
    }

    if (
      missingRowIds.length === 0 &&
      unindexedRowIds.length === 0 &&
      rowOrder.length === shard.rowCount &&
      boundaryMetadataMatches
    ) {
      continue;
    }

    return {
      message: [
        `Database \"${this.databaseId}\" cannot load because row shard `,
        `\"${shard.shardId}\" is inconsistent: manifest expects `,
        `${shard.rowCount} rows, row order contains ${rowOrder.length}, `,
        `row storage contains ${rowsMap.size}, and boundary metadata `,
        `${boundaryMetadataMatches ? 'matches' : 'does not match'} the stored order.`,
      ].join(''),
      repairable: missingRowIds.length === 0 && unindexedRowIds.length === 0,
      rowCount: rowOrder.length,
      shardId: shard.shardId,
    };
  }

  return null;
}

export function getAuthoritativeRowShardIntegrityIssue(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseRowShardIntegrityIssue | null {
  const issue = this.getLoadedRowShardIntegrityIssue(manifest);
  if (!issue) {
    this._rowShardAuthoritativeIntegritySnapshot = undefined;
    return null;
  }
  if (!hasSystemDocBarrier(this._hostModel.store)) {
    return issue;
  }
  const snapshot = this.captureLoadedRowShardSnapshot(manifest);
  return snapshot && snapshot === this._rowShardAuthoritativeIntegritySnapshot
    ? issue
    : null;
}

export function getRowShardHydrationProgress(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseRowShardHydrationProgress {
  let loadedRows = 0;
  let readyShards = 0;
  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) continue;
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    loadedRows += Math.min(shard.rowCount, rowOrder.length, rowsMap.size);
    if (this.hasCompleteLoadedShardRows(shard, shardDoc)) {
      readyShards += 1;
    }
  }
  return {
    loadedRows,
    readyShards,
    totalRows: this.getManifestRowCount(manifest),
    totalShards: manifest.shards.length,
  };
}

export function captureLoadedRowShardSnapshot(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  _includeEmptyManifestMismatch = false
): string | null {
  const shards: Array<{
    docId: string;
    rowOrder: string[];
    rowStorageIds: string[];
    stateVector: string;
  }> = [];

  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) return null;
    const rowOrder = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(value => String(value));
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    shards.push({
      docId: shardDoc.id,
      rowOrder,
      rowStorageIds: [...rowsMap.keys()].map(String).sort(),
      stateVector: encodeStateVector(shardDoc.spaceDoc).toString(),
    });
  }

  return JSON.stringify({
    manifest,
    rootStateVector: encodeStateVector(this.storageDoc.spaceDoc).toString(),
    shards,
  });
}

export function scheduleLoadedEmptyRowShardConfirmation(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  issue: DatabaseRowShardIntegrityIssue,
  beforeSnapshot: string | null
): void {
  const hasBarrier = hasSystemDocBarrier(this._hostModel.store);
  if (
    this._disposed ||
    this.readonly$.value ||
    !issue.repairable ||
    !beforeSnapshot ||
    !hasBarrier ||
    this._rowShardAutoRepairInFlight ||
    this._rowShardEmptyConfirmationAttemptedSnapshot === beforeSnapshot
  ) {
    return;
  }

  this._rowShardEmptyConfirmationAttemptedSnapshot = beforeSnapshot;
  const abort = new AbortController();
  this._rowShardAutoRepairAbort?.abort();
  this._rowShardAutoRepairAbort = abort;
  const context: SystemDocBarrierContext = {
    rootDocId: this.storageDoc.id,
    docIds: manifest.shards.map(shard => shard.serverDocId),
    signal: abort.signal,
  };

  this._rowShardAutoRepairInFlight = runSystemDocBarrier(
    this._hostModel.store,
    context
  )
    .then(() => {
      if (this._disposed || abort.signal.aborted || this.readonly$.value) {
        return;
      }
      const currentManifest = requireLoadedShardDirectory(
        this._model,
        this.databaseId
      ).manifest;
      const currentIssue = this.getLoadedRowShardIntegrityIssue(
        currentManifest,
        true
      );
      const afterSnapshot = this.captureLoadedRowShardSnapshot(
        currentManifest,
        true
      );
      if (
        currentIssue?.repairable &&
        currentIssue.message === issue.message &&
        afterSnapshot === beforeSnapshot
      ) {
        this._rowShardEmptyConfirmedSnapshot = beforeSnapshot;
      }
    })
    .catch(() => {
      // Keep the shard in loading state. A failed confirmation barrier never
      // authorizes a manifest rewrite from an unconfirmed empty snapshot.
    })
    .finally(() => {
      if (this._rowShardAutoRepairAbort === abort) {
        this._rowShardAutoRepairAbort = undefined;
      }
      this._rowShardAutoRepairInFlight = undefined;
      this._shardedRowsRevision.value += 1;
    });
}

export function scheduleLoadedRowShardIntegrityValidation(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  issue: DatabaseRowShardIntegrityIssue
): void {
  const hasBarrier = hasSystemDocBarrier(this._hostModel.store);
  const beforeSnapshot = this.captureLoadedRowShardSnapshot(manifest);
  if (
    this._disposed ||
    !beforeSnapshot ||
    !hasBarrier ||
    this._rowShardIntegrityValidationInFlight ||
    this._rowShardIntegrityValidationAttemptedSnapshot === beforeSnapshot
  ) {
    return;
  }

  this._rowShardIntegrityValidationAttemptedSnapshot = beforeSnapshot;
  const abort = new AbortController();
  this._rowShardIntegrityValidationAbort?.abort();
  this._rowShardIntegrityValidationAbort = abort;
  const context: SystemDocBarrierContext = {
    rootDocId: this.storageDoc.id,
    docIds: manifest.shards.map(shard => shard.serverDocId),
    signal: abort.signal,
  };

  this._rowShardIntegrityValidationInFlight = runSystemDocBarrier(
    this._hostModel.store,
    context
  )
    .then(() => {
      if (this._disposed || abort.signal.aborted) return;
      const currentManifest = requireLoadedShardDirectory(
        this._model,
        this.databaseId
      ).manifest;
      const currentIssue =
        this.getLoadedRowShardIntegrityIssue(currentManifest);
      const afterSnapshot = this.captureLoadedRowShardSnapshot(currentManifest);
      if (!currentIssue || currentIssue.message !== issue.message) return;
      if (afterSnapshot === beforeSnapshot) {
        this._rowShardAuthoritativeIntegritySnapshot = afterSnapshot;
        this._rowShardIntegrityError.value = currentIssue.message;
        this.queueShardedRowsRefresh();
      }
    })
    .catch(() => {
      // A later consumer read may retry after a transient barrier failure.
      if (
        !this._disposed &&
        !abort.signal.aborted &&
        this._rowShardIntegrityValidationAttemptedSnapshot === beforeSnapshot
      ) {
        this._rowShardIntegrityValidationAttemptedSnapshot = undefined;
      }
    })
    .finally(() => {
      if (this._rowShardIntegrityValidationAbort === abort) {
        this._rowShardIntegrityValidationAbort = undefined;
      }
      this._rowShardIntegrityValidationInFlight = undefined;
    });
}

export function scheduleLoadedRowShardManifestAutoRepair(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest,
  issue: DatabaseRowShardIntegrityIssue
): void {
  // The preloader is owned by the visible host page. Sharded databases keep
  // durable storage in a separate root Store, so looking up by _model.store
  // disconnects the production path while same-store tests still pass.
  const hasBarrier = hasSystemDocBarrier(this._hostModel.store);
  const beforeSnapshot = this.captureLoadedRowShardSnapshot(manifest, true);
  if (
    this._disposed ||
    this.readonly$.value ||
    !issue.repairable ||
    !beforeSnapshot ||
    !hasBarrier ||
    this._rowShardAutoRepairInFlight ||
    this._rowShardAutoRepairAttemptedSnapshot === beforeSnapshot
  ) {
    return;
  }

  this._rowShardAutoRepairAttemptedSnapshot = beforeSnapshot;
  const abort = new AbortController();
  this._rowShardAutoRepairAbort?.abort();
  this._rowShardAutoRepairAbort = abort;
  const context: SystemDocBarrierContext = {
    rootDocId: this.storageDoc.id,
    docIds: manifest.shards.map(shard => shard.serverDocId),
    signal: abort.signal,
  };
  const crossDocSyncBarrier = () =>
    runSystemDocBarrier(this._hostModel.store, context);

  this._rowShardAutoRepairInFlight = crossDocSyncBarrier()
    .then(async () => {
      if (this._disposed || abort.signal.aborted || this.readonly$.value) {
        return;
      }
      const currentManifest = requireLoadedShardDirectory(
        this._model,
        this.databaseId
      ).manifest;
      const currentIssue = this.getLoadedRowShardIntegrityIssue(
        currentManifest,
        true
      );
      const afterSnapshot = this.captureLoadedRowShardSnapshot(
        currentManifest,
        true
      );
      if (
        !currentIssue?.repairable ||
        currentIssue.message !== issue.message ||
        afterSnapshot !== beforeSnapshot
      ) {
        return;
      }
      await this.repairLoadedRowShardManifestDrift(beforeSnapshot);
      // The same canonical barrier now flushes the loaded-shard delta and
      // the root-manifest update, then waits for backend ACK/global sync
      // quiescence. This closes the outbox boundary without inventing a
      // second persistence writer or merging a fresh full-state Y.Doc.
      await crossDocSyncBarrier();
      const repairedManifest = requireCompleteShardDirectory(
        this._model,
        this.databaseId
      );
      if (this.getLoadedRowShardIntegrityIssue(repairedManifest, true)) {
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          'Database row-shard auto-repair did not reach a consistent synced readback.'
        );
      }
    })
    .catch(() => {
      // The database-local error and manual recovery action remain visible.
      // A failed/aborted barrier never broadens into an unfenced write.
    })
    .finally(() => {
      if (this._rowShardAutoRepairAbort === abort) {
        this._rowShardAutoRepairAbort = undefined;
      }
      this._rowShardAutoRepairInFlight = undefined;
    });
}

export async function repairLoadedRowShardManifestDrift(
  this: DatabaseSourceRuntime,
  expectedSnapshot?: string
): Promise<DatabaseRowShardRepairResult> {
  if (this.readonly$.value) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database row-shard repair requires edit permission.'
    );
  }
  const directory = this.getCompleteShardDirectory();
  // A repair rewrites every shard entry; it waits for the complete list.
  if (directory.status !== 'ready') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database rows are still loading; repair runs once they are loaded.'
    );
  }
  const manifest = directory.manifest;
  if (
    expectedSnapshot !== undefined &&
    this.captureLoadedRowShardSnapshot(manifest, true) !== expectedSnapshot
  ) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database row-shard snapshot changed before repair; refusing stale metadata repair.'
    );
  }

  const repairPlans = manifest.shards.map(shard => {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row shard "${shard.shardId}" is not ready for repair.`
      );
    }
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const storedRowOrder = rowOrder.toArray().map(value => String(value));
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const canonicalRowOrder = canonicalDatabaseRowOrder(storedRowOrder);
    const uniqueRowIds = new Set(canonicalRowOrder);
    const rowOrderMatchesStorage =
      storedRowOrder.every(rowId => rowsMap.has(rowId)) &&
      [...rowsMap.keys()].every(rowId => uniqueRowIds.has(String(rowId)));
    if (!rowOrderMatchesStorage) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row shard \"${shard.shardId}\" cannot be repaired automatically because row order and row storage differ.`
      );
    }
    const manifestMatchesCanonicalOrder =
      shard.rowCount === canonicalRowOrder.length &&
      shard.firstRowId === canonicalRowOrder[0] &&
      shard.lastRowId === canonicalRowOrder.at(-1);
    return {
      canonicalRowOrder,
      hasDuplicateRowIds: canonicalRowOrder.length !== storedRowOrder.length,
      manifestMatchesCanonicalOrder,
      rowOrder,
      rowsMap,
      shard,
      shardDoc,
    };
  });

  const repairedShardCount = repairPlans.filter(
    plan => plan.hasDuplicateRowIds || !plan.manifestMatchesCanonicalOrder
  ).length;
  const rowCount = repairPlans.reduce(
    (count, plan) => count + plan.canonicalRowOrder.length,
    0
  );

  if (repairedShardCount === 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'No safely repairable row-shard manifest drift is currently loaded.'
    );
  }

  const changes = new Map<
    Parameters<DatabaseRowMutation['changes']['get']>[0],
    DatabaseRowMutation['changes'] extends ReadonlyMap<unknown, infer Value>
      ? Value
      : never
  >();
  for (const plan of repairPlans) {
    const row = (rowId: string) => {
      const cells = plan.rowsMap.get(rowId);
      if (!(cells instanceof YMap))
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          'Database repair row is unavailable.'
        );
      return { rowId, cells: structuredClone(cells.toJSON()) as RowShardCells };
    };
    changes.set(plan.shard, {
      doc: plan.shardDoc,
      before: plan.rowOrder.toArray().map(value => row(String(value))),
      rows: plan.canonicalRowOrder.map(row),
    });
  }
  this.captureStorageSync();
  const committed = this.commitRowMutation({
    kind: 'move',
    changes,
    interactive: expectedSnapshot === undefined,
  });
  await claimRowPublication(this, committed);
  const repairedManifest = requireCompleteShardDirectory(
    this._model,
    this.databaseId
  );

  for (const [index, plan] of repairPlans.entries()) {
    const readbackOrder = plan.rowOrder.toArray().map(rowId => String(rowId));
    const readbackUnique = new Set(readbackOrder);
    const readbackManifest = repairedManifest.shards[index];
    if (
      readbackOrder.length !== readbackUnique.size ||
      readbackOrder.length !== plan.rowsMap.size ||
      readbackOrder.length !== readbackManifest?.rowCount ||
      readbackManifest.firstRowId !== readbackOrder[0] ||
      readbackManifest.lastRowId !== readbackOrder.at(-1)
    ) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row shard "${plan.shard.shardId}" repair readback failed.`
      );
    }
  }
  this._rowShardIntegrityError.value = null;
  this._rowShardAutoRepairAttemptedSnapshot = undefined;
  this._rowShardIntegrityValidationAttemptedSnapshot = undefined;
  this._rowShardAuthoritativeIntegritySnapshot = undefined;
  this._rowShardEmptyConfirmationAttemptedSnapshot = undefined;
  this._rowShardEmptyConfirmedSnapshot = undefined;
  this.refreshShardedRowsFromDocs(repairedManifest.shards);

  return { repairedShardCount, rowCount };
}

export function areAllManifestShardRowsMaterialized(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>
): boolean {
  if (!manifest) return false;
  return manifest.shards.every(shard => {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) return false;
    return this.hasCompleteLoadedShardRows(shard, shardDoc);
  });
}
